import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import {
  PhHouse,
  PhGraduationCap,
  PhFirstAidKit,
  PhLifebuoy,
  PhHandCoins,
  PhGlobe,
  PhMegaphone,
  PhBookOpenText,
  PhHeadset,
  PhQuestionMark,
  PhSun,
  PhShieldCheck,
  PhRocketLaunch,
  PhSealQuestion,
  PhTarget,
  PhBookOpen,
  PhScales,
} from 'ember-phosphor-icons';
import BetaHomeAlertRow from 'tio-employee/components/beta/home/alert-row';
import BetaHomeVerifyPhoneModal from 'tio-employee/components/beta/home/verify-phone-modal';
import BetaHomeDiscoverCard from 'tio-employee/components/beta/home/discover-card';
import BetaHomeProgressCard from 'tio-employee/components/beta/home/progress-card';
import SiteFooter from 'tio-common/components/site-footer';
import BetaHomeStatCard from 'tio-employee/components/beta/home/stat-card';
import { buildBetaHomeModel } from 'tio-employee/utils/beta/home-model';
import { betaHomeFlags } from 'tio-employee/utils/beta/home-flags';
import { benefitFlags } from 'tio-employee/utils/beta/benefit-flags';
import { benefitRows } from 'tio-employee/utils/beta/benefit-rows';
import type {
  BetaDiscoverCard,
  BetaHomeFlags,
  BetaHomeModel,
  BetaIcon,
  BetaProgressCard,
} from 'tio-employee/utils/beta/home-model';
import type { HomeTwentyTwentySixModel } from 'tio-employee/routes/authenticated/reimagined/home-2026';
import type FeaturesService from 'tio-common/services/features';
import type { IntlService } from 'ember-intl';
import type BetaBrandService from 'tio-employee/services/beta-brand';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import { phoneCaptureCard } from 'tio-employee/utils/beta/phone-capture-card';

// Home-2026 follows the Brand Updates design and uses a forced-dark full-viewport shell.
// Loan Wellness and Secure Your Future destinations keep the rail/grid aligned; remaining gaps live in BETA_HOME_GAPS.md.

/**
 * The design draws the rail with every destination present, because it depicts
 * a fully-entitled user. `flag` is NOT a design change — it is the
 * scenario-awareness rule every reimagined surface must follow
 * (reimagined-data.instructions.md): a `ta-only` partner following "Loan
 * Repayment" lands on a benefit they do not have. Items with no flag are
 * unconditional, exactly as drawn.
 */
interface NavItem {
  icon: BetaIcon;
  label: string;
  route: string;
  flag?: keyof BetaHomeFlags;
  /** The design mirrors the Megaphone glyph; nothing else is flipped. */
  mirrored?: boolean;
}

const BENEFITS_NAV: NavItem[] = [
  {
    icon: PhGraduationCap,
    label: 'Tuition Assistance',
    route: 'authenticated.tas.dashboard',
    flag: 'showTuitionAssistance',
  },
  {
    // Financial wellness is its own entitlement (partner.hasWellness), not a
    // loan sub-benefit — the built-in `wellness` scenario turns it on with
    // loans off.
    icon: PhFirstAidKit,
    label: 'Loan Wellness',
    route: 'authenticated.wellness-insights',
    flag: 'showFinancialWellness',
  },
  {
    icon: PhLifebuoy,
    label: 'Loan Forgiveness',
    route: 'authenticated.pslf.index',
    flag: 'showPslf',
  },
  {
    icon: PhHandCoins,
    label: 'Loan Repayment',
    route: 'authenticated.slr.dashboard',
    flag: 'showStudentLoanRepayment',
  },
  {
    icon: PhGlobe,
    label: 'Tuition Discounts',
    route: 'authenticated.lpn.index',
    flag: 'showLpn',
  },
  {
    // Not in the concept's rail (header, change 4). The grid already offers a
    // Secure Your Future card, so without this the only route to the benefit is
    // a card that scrolls away.
    icon: PhRocketLaunch,
    label: 'Secure Your Future',
    route: 'authenticated.syf.dashboard',
    flag: 'showSyf',
  },
];

const RESOURCES_NAV: NavItem[] = [
  {
    icon: PhMegaphone,
    label: 'Coaching',
    route: 'authenticated.tas.mentoring',
    flag: 'showMentoring',
    mirrored: true,
  },
  { icon: PhBookOpenText, label: 'FAQs', route: 'authenticated.questions-you-have' },
  { icon: PhHeadset, label: 'Support', route: 'authenticated.contact-us' },
  { icon: PhQuestionMark, label: 'Help', route: 'authenticated.questions-you-have.glossary' },
];

/**
 * "More to Explore" and "Resources" carry no live data, so they stay exactly as
 * authored.
 *
 * GAP (syf-match): "$800/year" is the design's figure. The real number is the
 * person's employer 401(k) match cap, which no endpoint exposes on this screen
 * (BETA_HOME_GAPS.md). Kept because this page is the design reference — it
 * needs a real source before the screen reaches real users.
 */
/** A design card plus the entitlement that governs its destination. */
type DiscoverEntry = BetaDiscoverCard & { flag?: keyof BetaHomeFlags };

const MORE_TO_EXPLORE: DiscoverEntry[] = [
  {
    // The one card not in the concept (header, change 3). Copy matches the
    // /dashboard card so the two surfaces say the same thing.
    id: 'financial-wellness',
    icon: PhFirstAidKit,
    title: 'Loan Wellness',
    description: 'See where your loans stand and what to do next.',
    meta: 'check your loan health',
    route: 'authenticated.wellness-insights',
    flag: 'showFinancialWellness',
  },
  {
    id: 'pslf',
    icon: PhShieldCheck,
    title: 'Loan Forgiveness (PSLF)',
    description: 'Qualifying employment can unlock your loan forgiveness.',
    meta: 'up to 100% forgiven',
    route: 'authenticated.pslf.index',
    flag: 'showPslf',
  },
  {
    id: 'lpn',
    icon: PhGlobe,
    title: 'Tuition Discounts',
    description: 'You may be eligible for discounts at top institutions!',
    meta: 'up to 25% off',
    route: 'authenticated.lpn.index',
    flag: 'showLpn',
  },
  {
    id: 'mentoring',
    icon: PhHeadset,
    title: 'Education Mentorship',
    description: "We're here to help you maximize your benefits.",
    meta: 'make an appointment',
    route: 'authenticated.tas.mentoring',
    flag: 'showMentoring',
  },
  {
    id: 'syf',
    icon: PhRocketLaunch,
    title: 'Secure Your Future',
    description: 'Qualifying loan payments can trigger a 401(k) match.',
    meta: '$800/year',
    route: 'authenticated.syf.dashboard',
    flag: 'showSyf',
  },
];

const RESOURCES: DiscoverEntry[] = [
  {
    id: 'faqs',
    icon: PhSealQuestion,
    title: 'FAQs',
    description: "Have a question? We likely have the answers you're looking for.",
    href: 'https://tio.zendesk.com/hc/en-us',
  },
  {
    id: 'strategy-finder',
    icon: PhTarget,
    title: 'Strategy Finder',
    description: 'Compare options and find the best plan for your situation.',
    route: 'authenticated.repaying-student-debt.repayment-strategy-finder',
  },
  {
    id: 'knowledge-base',
    icon: PhBookOpen,
    title: 'Knowledge Base',
    description: 'Learn more about the benefit programs available to you.',
    route: 'authenticated.questions-you-have.glossary',
  },
  {
    id: 'idr',
    icon: PhScales,
    title: 'Income Driven Repayment',
    description: 'Balance your payments so you only pay what you can afford.',
    route: 'authenticated.idr.dashboard',
    flag: 'showIdr',
  },
];

/**
 * The design's own progress-card captions, restored over the shared model's.
 *
 * GAP (ta-benefit-year): `computeTasUsedAmount` filters on the CALENDAR year
 * and sums approved rather than paid, so "Used This Benefit Year" describes the
 * number more generously than the data supports — which is why the /dashboard
 * treatment says "Approved This Year". This page keeps the design's wording;
 * closing the gap needs a benefit-year-scoped, paid-based total.
 */
const DESIGN_CAPTIONS: Record<string, string> = {
  'tuition-assistance': 'Used This Benefit Year',
  'forgiveness-progress': 'Loan Forgiveness Progress',
};

interface HomeTwentyTwentySixSignature {
  Args: { model: HomeTwentyTwentySixModel };
  Element: HTMLDivElement;
}

export default class HomeTwentyTwentySix extends Component<HomeTwentyTwentySixSignature> {
  @service declare partner: PartnerService;
  @service declare employee: EmployeeService;
  @service declare demoMode: DemoModeService;
  @service declare sessionContext: SessionContextService;
  @service('beta-brand') declare betaBrand: BetaBrandService;
  @service declare features: FeaturesService;
  @service declare intl: IntlService;

  @tracked isVerifyingPhone = false;

  /** The same rows the shipped side nav renders, so this page and the rail agree. */
  get flags(): BetaHomeFlags {
    const scenario = this.demoMode.scenarioFeatures;
    const rows = benefitRows(
      benefitFlags({
        partner: this.partner,
        employee: this.employee,
        sessionContext: this.sessionContext,
        wellnessData: this.args.model.wellnessData,
        scenario,
      })
    );
    return betaHomeFlags({ rows, partner: this.partner, features: this.features });
  }

  /** Reading the state here is what retires the row the moment a number is verified. */
  get phoneCapture() {
    return phoneCaptureCard(
      this.intl,
      this.sessionContext.currentEmployee?.phoneVerificationState ?? 'none'
    );
  }

  openAlertAction = () => {
    this.isVerifyingPhone = true;
  };

  closeVerifyPhone = () => {
    this.isVerifyingPhone = false;
  };

  /** Unflagged entries are unconditional, exactly as the design draws them. */
  #entitled = <T extends { flag?: keyof BetaHomeFlags }>(items: T[]): T[] =>
    items.filter((item) => !item.flag || this.flags[item.flag]);

  get benefitsNav(): NavItem[] {
    return this.#entitled(BENEFITS_NAV);
  }

  get resourcesNav(): NavItem[] {
    return this.#entitled(RESOURCES_NAV);
  }

  get moreToExplore(): DiscoverEntry[] {
    return this.#entitled(MORE_TO_EXPLORE);
  }

  get resources(): DiscoverEntry[] {
    return this.#entitled(RESOURCES);
  }

  get home(): BetaHomeModel {
    return buildBetaHomeModel({
      model: this.args.model.beta.reimagined,
      firstName: this.firstName,
      hour: new Date().getHours(),
      actionItems: this.args.model.beta.actionItems,
      flags: this.flags,
      phoneCapture: this.phoneCapture,
      tuitionYearToDate: this.args.model.beta.tuitionYearToDate,
    });
  }

  /** Live figures, the design's captions. */
  get progressCards(): BetaProgressCard[] {
    return this.home.progressCards.map((card) => {
      const caption = DESIGN_CAPTIONS[card.id];
      return caption ? { ...card, caption } : card;
    });
  }

  /** Same resolution as components/beta/home, fallback included. */
  get firstName(): string {
    return (
      this.sessionContext.user?.person?.firstName ||
      this.sessionContext.currentEmployee?.firstName ||
      ''
    );
  }

  get #lastName(): string {
    return this.sessionContext.user?.person?.lastName || '';
  }

  get fullName(): string {
    return [this.firstName, this.#lastName].filter(Boolean).join(' ');
  }

  get initials(): string {
    return [this.firstName, this.#lastName]
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  <template>
    {{! Hardcoded English, matching components/beta/home. The 2026 copy is still
        in design flux and every string would otherwise land in three locale
        files; BETA_HOME_GAPS.md (gap: i18n) tracks this as a hard gate on
        beta → GA. }}
    {{! template-lint-disable no-bare-strings }}
    <div
      class="tio-beta-home tio-beta-home--full"
      data-mode="dark"
      data-beta-brand={{this.betaBrand.brand}}
      data-test-beta-home-2026
      ...attributes
    >
      <nav class="tio-beta-home__nav" aria-label="Benefits and resources">
        <div class="tio-beta-home__nav-top">
          <LinkTo @route="authenticated.dashboard" class="tio-beta-home__nav-logo">
            <img src="/assets/images/brand-2026/logo-mark.svg" alt="" class="size-10" />
            <img src="/assets/images/brand-2026/logo-wordmark.svg" alt="Tuition.io" class="h-10" />
          </LinkTo>

          <ul class="tio-beta-home__nav-groups">
            <li>
              <LinkTo
                @route="authenticated.reimagined.home-2026"
                @activeClass="tio-beta-home__nav-link--active"
                class="tio-beta-home__nav-link"
                data-test-beta-nav="home"
              >
                <PhHouse @size={{20}} />
                <span>Home</span>
              </LinkTo>
            </li>

            <li class="tio-beta-home__nav-group">
              <div class="tio-beta-home__nav-heading" id="beta-nav-benefits">Benefits</div>
              <ul aria-labelledby="beta-nav-benefits">
                {{#each this.benefitsNav as |item|}}
                  <li>
                    <LinkTo
                      @route={{item.route}}
                      @activeClass="tio-beta-home__nav-link--active"
                      class="tio-beta-home__nav-link"
                    >
                      {{#let item.icon as |Icon|}}
                        <Icon @size={{20}} />
                      {{/let}}
                      <span>{{item.label}}</span>
                    </LinkTo>
                  </li>
                {{/each}}
              </ul>
            </li>

            <li class="tio-beta-home__nav-group">
              <div class="tio-beta-home__nav-heading" id="beta-nav-resources">Resources</div>
              <ul aria-labelledby="beta-nav-resources">
                {{#each this.resourcesNav as |item|}}
                  <li>
                    <LinkTo
                      @route={{item.route}}
                      @activeClass="tio-beta-home__nav-link--active"
                      class="tio-beta-home__nav-link"
                    >
                      {{#let item.icon as |Icon|}}
                        <Icon
                          @size={{20}}
                          class={{if item.mirrored "tio-beta-home__nav-icon--mirrored"}}
                        />
                      {{/let}}
                      <span>{{item.label}}</span>
                    </LinkTo>
                  </li>
                {{/each}}
              </ul>
            </li>
          </ul>
        </div>

        {{#if this.fullName}}
          <LinkTo @route="authenticated.profile" class="tio-beta-home__nav-user">
            <span class="tio-beta-home__nav-avatar" aria-hidden="true">{{this.initials}}</span>
            <span>{{this.fullName}}</span>
          </LinkTo>
        {{/if}}
      </nav>

      <div class="tio-beta-home__full-body">
        <div class="tio-beta-home__main">
          <h1 class="sr-only">Home</h1>

          <section class="tio-beta-home__section">
            <div class="tio-beta-home__hero tio-beta-home__hero--active">
              <div class="tio-beta-home__hero-head">
                <div class="tio-col-tight">
                  <div class="tio-beta-home__greeting">
                    <PhSun @size={{20}} />
                    <span>{{this.home.greeting}}, {{this.home.firstName}}</span>
                  </div>
                  <p class="tio-beta-home__title">Your Education Benefits Dashboard</p>
                </div>

                {{#if this.partner.publicTheme}}
                  <Button @intent="primary" @href="#more-to-explore">View Benefits</Button>
                {{else}}
                  <a href="#more-to-explore" class="app-text-link app-text-link--icon">View Benefits</a>
                {{/if}}
              </div>

              <div class="tio-beta-home__grid tio-beta-home__grid--quad">
                {{#each this.home.stats as |stat|}}
                  <BetaHomeStatCard @stat={{stat}} />
                {{/each}}
              </div>
            </div>
          </section>

          {{#if this.home.alerts.length}}
            <section class="tio-beta-home__section tio-beta-home__section--attention">
              <div class="tio-beta-home__attention-head">
                <h2 class="tio-beta-home__section-title">Needs Your Attention</h2>
                <span class="tio-beta-home__attention-count">({{this.home.alerts.length}})</span>
              </div>

              <div class="tio-beta-home__attention">
                <div class="tio-col-tight">
                  {{#each this.home.alerts as |alert|}}
                    <BetaHomeAlertRow @alert={{alert}} @onAction={{this.openAlertAction}} />
                  {{/each}}
                </div>
              </div>
            </section>
          {{/if}}

          {{#if this.progressCards.length}}
            <section class="tio-beta-home__section">
              <h2 class="tio-beta-home__section-title">Active Benefits</h2>
              <div class="tio-beta-home__grid">
                {{#each this.progressCards as |card|}}
                  <BetaHomeProgressCard @card={{card}} />
                {{/each}}
              </div>
            </section>
          {{/if}}

          <section id="more-to-explore" class="tio-beta-home__section">
            <h2 class="tio-beta-home__section-title">More to Explore</h2>
            <div class="tio-beta-home__grid tio-beta-home__grid--quad">
              {{#each this.moreToExplore as |card|}}
                <BetaHomeDiscoverCard @card={{card}} />
              {{/each}}
            </div>
          </section>

          <section class="tio-beta-home__section tio-beta-home__section--last">
            <h2 class="tio-beta-home__section-title">Resources</h2>
            <div class="tio-beta-home__grid tio-beta-home__grid--quad">
              {{#each this.resources as |card|}}
                <BetaHomeDiscoverCard @card={{card}} />
              {{/each}}
            </div>
          </section>
        </div>

        {{! ─── Footer ───
             GAP (partner-tagline): the design carries "Partner Tagline can be
             configurable by partner." There is no tagline field on company
             settings, so the shared footer omits the row for real users. This
             page passes the design's placeholder through, since it is the
             design reference. }}
        <SiteFooter
          @brand={{this.betaBrand.brand}}
          @tagline="Partner Tagline can be configurable by partner."
        />
      </div>
    </div>

    <BetaHomeVerifyPhoneModal
      @isOpen={{this.isVerifyingPhone}}
      @onClose={{this.closeVerifyPhone}}
      @employee={{this.sessionContext.currentEmployee}}
    />
  </template>
}
